import { PERMISSION_ACTION_LABELS, PERMISSION_MODULE_ACTIONS, PERMISSION_MODULE_LABELS, type PermissionAction, type PermissionItem, type PermissionModule } from '../../types';
import styles from './PermissionMatrixEditor.module.css';

const ALL_ACTIONS: PermissionAction[] = ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT'];
const ALL_MODULES = Object.keys(PERMISSION_MODULE_ACTIONS) as PermissionModule[];

interface PermissionMatrixEditorProps {
    value: PermissionItem[];
    onChange?: (next: PermissionItem[]) => void;
    disabled?: boolean;
    /**
     * Celulas `modulo:acao` a destacar como excecao (tela de Usuarios: o que
     * difere do perfil do usuario).
     */
    destaques?: Set<string>;
    /**
     * Quando informado, o clique numa celula chama isto em vez de inverter o
     * valor por conta propria. A tela de Usuarios usa para decidir se o clique
     * cria ou apaga uma excecao.
     */
    onToggle?: (module: PermissionModule, action: PermissionAction) => void;
}

/**
 * Grade módulo (linha) x ação (coluna) usada tanto no editor de perfil de
 * acesso (`AdminAccessProfiles`) quanto, em modo `disabled`, para mostrar a
 * matriz efetiva de um usuário numa filial (`AdminUsers`).
 *
 * Células cuja ação não se aplica ao módulo (ex.: DASHBOARD não tem
 * CREATE/EDIT/DELETE) aparecem como "—", não como checkbox desmarcado —
 * evita confundir "não permitido" com "não existe essa ação aqui".
 */
export function PermissionMatrixEditor({ value, onChange, disabled, destaques, onToggle }: PermissionMatrixEditorProps) {
    const isAllowed = (module: PermissionModule, action: PermissionAction) =>
        value.find((p) => p.module === module && p.action === action)?.allowed ?? false;

    const toggle = (module: PermissionModule, action: PermissionAction) => {
        if (onToggle) {
            onToggle(module, action);
            return;
        }
        if (!onChange) return;
        const current = isAllowed(module, action);
        const withoutCell = value.filter((p) => !(p.module === module && p.action === action));
        onChange([...withoutCell, { module, action, allowed: !current }]);
    };

    return (
        <div className={styles.wrapper}>
            <table className={styles.matrix}>
                <thead>
                    <tr>
                        <th className={styles.moduleHeader}>Módulo</th>
                        {ALL_ACTIONS.map((action) => (
                            <th key={action}>{PERMISSION_ACTION_LABELS[action]}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {ALL_MODULES.map((module) => (
                        <tr key={module}>
                            <td className={styles.moduleName}>{PERMISSION_MODULE_LABELS[module]}</td>
                            {ALL_ACTIONS.map((action) => {
                                const applies = PERMISSION_MODULE_ACTIONS[module].includes(action);
                                if (!applies) {
                                    return <td key={action} className={styles.na}>—</td>;
                                }
                                const excecao = destaques?.has(`${module}:${action}`) ?? false;
                                return (
                                    <td
                                        key={action}
                                        className={excecao ? styles.excecao : undefined}
                                        title={excecao ? 'Exceção deste usuário: diferente do perfil' : undefined}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isAllowed(module, action)}
                                            disabled={disabled}
                                            onChange={() => toggle(module, action)}
                                            aria-label={`${PERMISSION_MODULE_LABELS[module]}: ${PERMISSION_ACTION_LABELS[action]}`}
                                        />
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
